import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';
import { getInitials } from '../../utils/helpers';
import './Sidebar.css';

const Sidebar = ({ isAdmin = false, collapsed, onToggle }) => {
  const location = useLocation();

  const { user, hasFeature } = useAuth();
  const { admin, hasPermission } = useAdmin();

  const isActive = (path) => location.pathname === path;

  const userMenuItems = [
    {
      path: '/connect-whatsapp',
      icon: 'fab fa-whatsapp',
      label: 'Connect WhatsApp',
      enabled: !user?.whatsappConnected
    },
    {
      path: '/dashboard',
      icon: 'fas fa-tachometer-alt',
      label: 'Dashboard',
      enabled: user?.whatsappConnected
    },
    {
      path: '/chat',
      icon: 'fas fa-comments',
      label: 'Chat',
      enabled: user?.whatsappConnected
    },
    {
      path: '/campaigns',
      icon: 'fas fa-bullhorn',
      label: 'Campaigns',
      enabled: user?.whatsappConnected && hasFeature('campaigns')
    },
    {
      path: '/contacts',
      icon: 'fas fa-address-book',
      label: 'Contacts',
      enabled: user?.whatsappConnected && hasFeature('contacts')
    },
    {
      path: '/templates',
      icon: 'fas fa-file-alt',
      label: 'Templates',
      enabled: user?.whatsappConnected && hasFeature('templates')
    },
    {
      path: '/media-library',
      icon: 'fas fa-photo-video',
      label: 'Media Library',
      enabled: user?.whatsappConnected
    },
    {
      path: '/automation',
      icon: 'fas fa-robot',
      label: 'Automation',
      enabled: user?.whatsappConnected && hasFeature('automation')
    },
    {
      path: '/analytics',
      icon: 'fas fa-chart-pie',
      label: 'Campaign Stats',
      enabled: user?.whatsappConnected && hasFeature('analytics')
    },
    {
      path: '/insights',
      icon: 'fas fa-chart-line',
      label: 'Business Insights',
      enabled: user?.whatsappConnected && hasFeature('business_insights')
    },
    {
      path: '/flows/whatsapp',
      icon: 'fas fa-list-check',
      label: 'WhatsApp Flows',
      enabled: user?.whatsappConnected && hasFeature('whatsapp_flows')
    },
    {
      path: '/ads/ctwa',
      icon: 'fas fa-bullhorn',
      label: 'Ad Manager',
      enabled: user?.whatsappConnected && hasFeature('ad_manager')
    },
    {
      path: '/reports',
      icon: 'fas fa-file-invoice',
      label: 'Reports',
      enabled: user?.whatsappConnected && hasFeature('reports')
    },
    {
      path: '/wallet',
      icon: 'fas fa-wallet',
      label: 'Wallet',
      enabled: true
    },
    {
      path: '/settings',
      icon: 'fas fa-cog',
      label: 'Settings',
      enabled: true
    }
  ];

  const adminMenuItems = [
    {
      path: '/admin/dashboard',
      icon: 'fas fa-tachometer-alt',
      label: 'Dashboard',
      enabled: true
    },
    {
      path: '/admin/users',
      icon: 'fas fa-users',
      label: 'User Management',
      enabled: hasPermission('user_management')
    },
    {
      path: '/admin/analytics',
      icon: 'fas fa-chart-line',
      label: 'Analytics',
      enabled: hasPermission('reports_access')
    },
    {
      path: '/admin/features',
      icon: 'fas fa-toggle-on',
      label: 'Feature Management',
      enabled: hasPermission('feature_management')
    },
    {
      path: '/admin/wallet',
      icon: 'fas fa-wallet',
      label: 'Wallet Management',
      enabled: hasPermission('wallet_management')
    },
    {
      path: '/admin/settings',
      icon: 'fas fa-cog',
      label: 'System Settings',
      enabled: hasPermission('system_settings')
    },
    {
      path: '/admin/admins',
      icon: 'fas fa-shield-alt',
      label: 'Admin Management',
      enabled: hasPermission('admin_management')
    }
  ];

  const menuItems = isAdmin ? adminMenuItems : userMenuItems;
  const currentUser = isAdmin ? admin : user;

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {/* Logo */}
      <div className="sidebar-logo">
        <Link to={isAdmin ? "/admin/dashboard" : "/dashboard"} className="logo-content">
          <img src="/logo.svg" alt="MessagePe" className="sidebar-logo-img" />
        </Link>
        <button
          className="sidebar-toggle"
          onClick={onToggle}
        >
          <i className={`fas fa-chevron-${collapsed ? 'right' : 'left'}`}></i>
        </button>
      </div>

      <div className="sidebar-user">
        <div className="user-avatar">
          {currentUser?.profilePicture ? (
            <img
              src={currentUser.profilePicture}
              alt={currentUser.name || 'User'}
            />
          ) : (
            <div className="avatar-initials">
              {getInitials(currentUser?.name)}
            </div>
          )}
        </div>
        {!collapsed && (
          <div className="user-info">
            <div className="user-name">{currentUser?.name}</div>
            <div className="user-meta">
              <span className="user-role">{isAdmin ? admin?.role?.replace('_', ' ') : 'User'}</span>
              {!isAdmin && user?.walletBalance !== undefined && (
                <>
                  <span className="meta-separator">•</span>
                  <span className="user-balance">₹{user.walletBalance.toFixed(2)}</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="sidebar-nav">
        <ul className="nav-menu">
          {menuItems.map((item, index) => {
            if (!item.enabled) return null;

            return (
              <li key={index} className="nav-item">
                <Link
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                  title={collapsed ? item.label : ''}
                >
                  <i className={item.icon}></i>
                  {!collapsed && <span className="nav-text">{item.label}</span>}
                  {isActive(item.path) && <div className="active-indicator"></div>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="sidebar-footer">
        {!collapsed && (
          <div className="footer-links">
            <a href="mailto:hey@vrittas.com" className="footer-link">
              <i className="fas fa-question-circle"></i>
              Help & Support
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;